import { startTransition, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getFavGameStart, getHomeDataStart } from 'store/redux-slices/casinoMenu'
import { getLanguageDataStart, getLanguagesStart, setSelectedLanguagesComplete } from 'store/redux-slices/language'
import { getUIConfigStart } from 'store/redux-slices/ui-config'
import { fetchRedirectionUrl } from 'utils/helpers'
import { getItem, setCookie, setItem, setRefreshToken } from 'utils/storageUtils'
import Loader from '../Loader'
import { extractLanguageCodeFromPath, getDefaultLanguage, getRedirectionUrl, getRootDomain, isValidLanguageCode } from 'utils/routes'
import { initiateSeon } from 'utils/seon'

/**
 * PreAppLoader is a React component that handles the initial loading state
 * of the application. It performs several critical functions:
 *
 * 1. Processes URL query parameters (token, userId, etc.)
 * 2. Loads necessary API data (languages, CMS pages)
 * 3. Validates the current route and language code
 * 4. Redirects to the appropriate URL if needed
 * 5. Ensures the application has all required data before rendering
 *
 * The component implements a robust language selection algorithm that considers:
 * - Saved language preference
 * - URL language code
 * - Browser language
 * - Default language
 *
 * It also validates routes against API data to ensure only valid CMS pages
 * are accessible, preventing issues like non-language route segments being
 * incorrectly stored as language codes.
 *
 * Example real-world scenario:
 * Currently, if a user tries to access the domain from Germany, they will be redirected to the mirror site if defined in the back office.
 * However, if they register with the India country code, they should be redirected to the country mirror site after registration, if defined for that country.
 * The user and token are retrieved from the registration response and passed to the appropriate URL (domain or the corresponding URL for that country).
 *
 * URL handling:
 * 1. For a URL like `https://yourapp.com/?token=abc123&userId=456`, the component detects the token and userId, stores them, and removes them from the URL.
 * 2. For a URL like `https://yourapp.com/privacy-policy`, the component validates if "privacy-policy" is a valid CMS slug and redirects to the appropriate language URL.
 * 3. For a URL with an invalid language code, the component redirects to the URL with the correct language code.
 *
 * @param {Object} props - The props for this component.
 * @param {React.ReactNode} props.children - The children components to render once loading is complete.
 * @returns {React.ReactNode} The loader or children depending on the loading state.
 */

const PreAppLoader = ({ children }) => {
  const dispatch = useDispatch()
  const { userDetails } = useSelector((state) => state.user)
  const { general: generalConfig, loading: uiConfigLoading } = useSelector((state) => state.uiConfig)

  const { languages, selectedLanguageCode } = useSelector(state => state.language)
  const { cmsList, casinoCategories } = useSelector(state => state.casinoMenu.gameCategory)

  const [isLoading, setIsLoading] = useState(true)
  const [routesReady, setRoutesReady] = useState(false)

  /**
   * Checks if the URL contains `token` and `userId` query parameters,
   * saves them to localStorage, and removes them from the URL.
   * @returns {boolean} Whether both `token` and `userId` were found in the URL.
   */
  const hasQueryParams = () => {
    // Extract token and userId from query parameters
    const urlParams = new URLSearchParams(window.location.search)
    const token = urlParams.get('redirectionToken')
    const userId = urlParams.get('userId')
    const countryCode = urlParams.get('countryCode')
    const refreshToken = urlParams.get('refreshToken')
    const sessionExpiry = urlParams.get('sessionExpiry')

    // If token and userId are in the query params, save them to localStorage
    if (token) setItem('access-token', token)
    if (userId) setItem('userId', userId)
    if (countryCode) setCookie('countryCode', countryCode, 365)
    if (userId && token) setItem('loggedIn', String(true))
    if (refreshToken) setRefreshToken(refreshToken)
    if (sessionExpiry) setItem('sessionExpiry', sessionExpiry, false)

    // Remove token and userId from the URL
    if (token || userId || countryCode) {
      urlParams.delete('token')
      urlParams.delete('userId')
      urlParams.delete('countryCode')
      urlParams.delete('refreshToken')
      urlParams.delete('sessionExpiry')

      // Only update the URL if there are remaining parameters
      if (urlParams?.toString()) {
        const newUrl = `${window.location.pathname}?${urlParams.toString()}`
        window.history.replaceState(null, '', newUrl)
      } else {
        // If no parameters remain, clear the query string
        const newUrl = `${window.location.pathname}`
        window.history.replaceState(null, '', newUrl)
      }
    }

    // Return whether query params exist
    return token && userId
  }

  /**
   * Fetches the redirection URL and performs the redirection if necessary.
   */
  const fetchRedirectionURL = async () => {
    // FIXME: Should be done through saga
    try {
      const accessToken = getItem('access-token')
      const userId = getItem('userId')
      const redirectionUrl = await fetchRedirectionUrl({ accessToken })
      const currentUrl = window.location.origin

      if (typeof redirectionUrl === 'string' &&
        redirectionUrl.trim() !== '' &&
        currentUrl !== redirectionUrl) {
        const pathName = window.location.pathname
        const urlParams = new URLSearchParams(window.location.search)
        const redirectionUrlWithParams = `${redirectionUrl}${pathName}?${urlParams.toString()}&redirectionToken=${encodeURIComponent(accessToken)}&userId=${encodeURIComponent(userId)}`
        if (accessToken) {
          window.location.href = redirectionUrlWithParams
        } else {
          const redirectionURL = `${redirectionUrl}${pathName}?${urlParams.toString()}`
          window.location.href = redirectionURL
        }
      } else {
        setIsLoading(false) // No redirection needed
      }
    } catch (error) {
      console.error('Error fetching redirection URL:', error)
      setIsLoading(false) // Proceed with the app even if there's an error
    }
  }

  useEffect(() => {
    // TODO: Refactor hasQueryParams function before removing its usage
    const hasParams = hasQueryParams()
    // Initiate Seon for analytics on page load (common for all tenants)
    initiateSeon()

    if (process.env.REACT_APP_ENABLE_MIRROR_REDIRECTION === 'true' ? hasParams : true) {
      // If token and userId or isRedirectUrl exist in query params, skip API call
      setIsLoading(false)
    } else {
      // Otherwise, fetch redirection URL
      fetchRedirectionURL()
    }
  }, [])

  useEffect(() => {
    if (!isLoading && generalConfig) {
      const languageConfig = generalConfig?.language || {}
      // If general config has the flag to override language, then we update the language list based on the languages
      // available in the config.
      const isOverrideLanguage = languageConfig?.isOverrideLanguage
      let allowedLanguageByConfig = []
      if (isOverrideLanguage) {
        const rootDomain = getRootDomain()
        if (languageConfig[rootDomain] && rootDomain !== process.env.REACT_APP_WEBSITE_DOMAIN) {
          allowedLanguageByConfig = languageConfig[rootDomain]
        } else {
          allowedLanguageByConfig = languageConfig?.main || []
        }
      }
      dispatch(getLanguagesStart({ allowedLanguageByConfig, isOverrideLanguage }))
      dispatch(getHomeDataStart({ casinoCategoriesLimit: 100 }))
    }
  }, [generalConfig, isLoading, dispatch])

  useEffect(() => {
    if (languages && cmsList && casinoCategories) {
      handleRouteValidation()
      setRoutesReady(true)
    }
  }, [languages, cmsList, casinoCategories])

  const handleRouteValidation = () => {
    const savedLanguage = getItem('language')?.toUpperCase()
    const urlPath = window.location.pathname
    const queryParams = window?.location?.search || ''
    // Resolved from the languages allowed on this domain rather than the build-time env alone:
    // mirrors share the bundle but can have a different, even non-English, default (AMGP-3718)
    const defaultLanguage = getDefaultLanguage(languages)

    const redirectionUrl = getRedirectionUrl(
      savedLanguage,
      urlPath,
      languages,
      cmsList,
      casinoCategories,
      defaultLanguage,
      queryParams
    )

    if (redirectionUrl) {
      const currentFullUrl = `${window.location.pathname}${window.location.search}`
      // Only update URL if it's actually different to avoid unnecessary operations
      if (currentFullUrl !== redirectionUrl) {
        // Use replaceState instead of window.location.href to avoid page reload
        window.history.replaceState(null, '', redirectionUrl)
      }
    }

    // Keep localStorage and Redux aligned with the language that ended up in the URL, so the
    // request header, the language switcher and the route agree without a full reload
    const resolvedLanguage = extractLanguageCodeFromPath((redirectionUrl || urlPath).split(/[?#]/)[0])
    if (isValidLanguageCode(resolvedLanguage, languages)) {
      if (resolvedLanguage !== savedLanguage) setItem('language', resolvedLanguage)
      if (resolvedLanguage !== selectedLanguageCode) {
        dispatch(setSelectedLanguagesComplete({ selectedLanguageCode: resolvedLanguage }))
      }
    }

    // Always dispatch language data after URL update
    dispatch(getLanguageDataStart())
  }

  useEffect(() => {
    if (userDetails?.userId) {
      dispatch(getFavGameStart({ page: 1, limit: 100 }))
    }
  }, [dispatch, userDetails?.userId])

  useEffect(() => {
    dispatch(getUIConfigStart())
  }, [dispatch])

  if (uiConfigLoading) {
    startTransition(() => {
      return <Loader top='40vh' />
    })
  }

  if (isLoading || !routesReady) {
    return <Loader top='40vh' />
  }

  return <>{children}</>
}

export default PreAppLoader
